"use client";

import type { ProductCard as ProductCardData } from "@ellaandil/formatters";
import { Add } from "@ellaandil/ui";
import { Suspense, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { ProductCard } from "@/components/ui/ProductCard";
import { CarouselTrack } from "@/components/ui/sections/_lib/CarouselTrack";
import { Typography } from "@/components/ui/Typography";
import { useTranslations } from "@/i18n/client";
import { useRecommendations } from "@/lib/depict/useRecommendations";
import { useCart } from "./hooks";
import { QuickAddPanel } from "./QuickAddPanel";

const UPSELL_LIMIT = 8;

type UpsellProduct = ProductCardData & {
	depictListingResultId?: string;
	depictProductId?: string;
};

/**
 * Renders Depict's `after_basket` recommendations under the cart line items.
 * Quietly hides itself if the cart is empty, Depict returns nothing, or the
 * request fails — the cart should still feel finished without it.
 *
 * Tapping a card's [+ ADD] reveals an inline `QuickAddPanel` so customers can
 * pick colour + size without leaving the drawer; the actual add-to-cart goes
 * through the shared buy-box flow on PDP.
 */
export const CartUpsell = () => {
	return (
		<Suspense fallback={null}>
			<CartUpsellInner />
		</Suspense>
	);
};

const CartUpsellInner = () => {
	const t = useTranslations();
	const { data: cart } = useCart();

	// Depict's `after_basket` is keyed on the items currently in the cart.
	// We send Centra item SKUs because that's how ellaandil's catalogue is
	// ingested into Depict (see lib/depict/product-id.ts) and what the PDP
	// tracking flow uses for variant attribution.
	const productIds = useMemo(
		() => (cart?.items ?? []).map((item) => item?.sku).filter((id): id is string => Boolean(id)),
		[cart?.items],
	);

	const { data } = useRecommendations({
		productIds,
		type: "after_basket",
		limit: UPSELL_LIMIT,
		enabled: productIds.length > 0,
	});

	const products = (data?.products ?? []) as UpsellProduct[];

	const [quickAddId, setQuickAddId] = useState<string | null>(null);
	const quickAddProduct = quickAddId ? products.find((p) => p.id === quickAddId) : undefined;

	// Skip the skeleton state entirely — Depict's `after_basket` may legitimately
	// return zero results (no rule configured for the current cart contents), and
	// flashing a skeleton row only to vanish a moment later reads as a bug. Once
	// data resolves we either mount cards or stay hidden.
	if (products.length === 0) return null;

	return (
		<section
			aria-label={t("You may also like")}
			className="flex flex-col gap-grid-component-spacing-md"
		>
			<Typography variant="body-sm-reg" as="h2" className="text-text-dark-text-1">
				{t("You may also like")}
			</Typography>
			<CarouselTrack
				layout="triplets"
				desktopAlwaysCarousel
				slotBeforeScrollbar={
					<QuickAddSlot product={quickAddProduct} onClose={() => setQuickAddId(null)} />
				}
			>
				{products.map((product) => (
					<UpsellItem
						key={product.id}
						product={product}
						onAdd={() => setQuickAddId(product.id ?? null)}
						isActive={product.id === quickAddId}
					/>
				))}
			</CarouselTrack>
		</section>
	);
};

const UpsellItem = ({
	product,
	onAdd,
	isActive,
}: {
	product: UpsellProduct;
	onAdd: () => void;
	isActive: boolean;
}) => {
	const t = useTranslations();
	return (
		<div className="flex flex-col gap-grid-component-spacing-2xs">
			<ProductCard product={product} size="small" showBrand={false} />
			{!isActive && (
				<Button
					variant="outline"
					color="primary"
					size="small"
					onClick={onAdd}
					aria-expanded={isActive}
					className="self-start"
				>
					<Add className="size-3" />
					{t("Add")}
				</Button>
			)}
		</div>
	);
};

/**
 * Animated mount/unmount wrapper for the QuickAddPanel. Uses the pure-CSS
 * grid-rows-[0fr]→[1fr] collapse trick so the panel slides open/closed
 * without a JS animation library, and latches onto the last product seen so
 * the exit animation still has content to render after `product` clears.
 */
const QuickAddSlot = ({
	product,
	onClose,
}: {
	product: UpsellProduct | undefined;
	onClose: () => void;
}) => {
	// Two pieces of state, intentionally decoupled:
	//   • `rendered` controls what's mounted (latches onto the last product so
	//     the exit animation has content to display after `product` clears)
	//   • `visible` drives the `data-state` attribute that the CSS transition
	//     keys off — flipped one frame after mount so the enter transition
	//     actually plays instead of snapping straight to the open state.
	const [rendered, setRendered] = useState<UpsellProduct | undefined>(undefined);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (product) {
			setRendered(product);
			const raf = requestAnimationFrame(() => setVisible(true));
			return () => cancelAnimationFrame(raf);
		}
		setVisible(false);
	}, [product]);

	if (!rendered) return null;

	return (
		<div
			data-state={visible ? "open" : "closed"}
			className="grid grid-rows-[0fr] opacity-0 transition-[grid-template-rows,opacity] duration-200 ease-out data-[state=open]:grid-rows-[1fr] data-[state=open]:opacity-100"
			onTransitionEnd={(e) => {
				// Only react to the grid-template-rows transition (opacity finishes
				// in the same frame and would otherwise fire a premature unmount).
				if (e.propertyName !== "grid-template-rows") return;
				if (!visible) setRendered(undefined);
			}}
		>
			<div className="overflow-hidden">
				<QuickAddPanel key={rendered.id} product={rendered} onClose={onClose} />
			</div>
		</div>
	);
};
